import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { DataGridPro, koKR, enUS, zhCN, useGridApiRef } from "@mui/x-data-grid-pro";
import { Button, TableContainer, Modal, CardContent, Chip } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import MainCard from "components/MainCard";
import useModals from 'components/modals/useModal';
import { modals } from 'components/modals/Modals';
import { addAllSpecKeys } from "components/axios/specAxios";

const theme = createTheme({
    components: {
        MuiDataGrid: {
            styleOverrides: {
                root: { border: 'none' },
                cell: { padding: '8px', fontSize: '14px' },
            },
        },
    },
});

const muiLocaleMap = {
  ko: koKR.components.MuiDataGrid.defaultProps.localeText,
  en: enUS.components.MuiDataGrid.defaultProps.localeText,
  'zh-CN': zhCN.components.MuiDataGrid.defaultProps.localeText,
};

const MasterSpecWriteLayer = ({ list, specKeyList }) => {
    const { t, i18n } = useTranslation();
    const [open, setOpen] = useState(false);

    const apiRef = useGridApiRef();
    const rowsRef = useRef([]);
    const [rows, setRows] = useState([]);
    const [deleting, setDeleting] = useState(false);

    const { openModal } = useModals();
    const handleAlertClick = (alertMessage) => {
        openModal(modals.alertModal, {
            onClose: () => { },
            message: alertMessage,
        });
    };

    const updateRows = (newRows) => {
        setRows(newRows);
        rowsRef.current = newRows;
    };

    useEffect(() => {
        if (list && list.length > 0) {
            const initialRows = list.map((data, idx) => ({
                uid: Date.now() + idx, // 고유 UID 필수
                ...data,
            }));
            updateRows(initialRows);
        }
    }, [list]);

    const handleSave = async () => {
        if (document.activeElement) document.activeElement.blur();

        setTimeout(() => {
            const updatedRows = rowsRef.current;

            const isBlank = updatedRows.some((row) => !row.name || !row.code);
            if (isBlank) {
                alert(t('message.common.error.text.spec.name.and.key.input'));
                return;
            }

            const body = updatedRows.map(({ uid, ...rest }) => rest);
            // console.log('-->',body)
            if (body.length === 0) {
                alert(t('message.common.alert.not.exist.save.contents'));
                return;
            }
            openModal(modals.confirmModal, {
                message: t('message.common.alert.apply.question'),
                onSubmit: async () => {
                    try {
                        await addAllSpecKeys(body);
                        handleAlertClick(t('message.common.alert.apply'));
                        setOpen(false);
                        specKeyList(0);
                    } catch (err) {
                        handleAlertClick(err.response?.data?.message || err.message);
                    }
                },
                onClose: () => {
                },
            });
        }, 50);
    };

    const handlePaste = async () => {
        try {
            // if (!navigator.clipboard || !navigator.clipboard.readText) {
            //     alert('클립보드 읽기를 지원하지 않는 브라우저입니다.');
            //     return;
            // }

            const text = await navigator.clipboard.readText();
            const newRows = text.trim().split("\n").map((line, idx) => {
                const [code = '', name = '', unit = ''] = line.split("\t");
                return { uid: Date.now() + idx, code, name, unit };
            });
            updateRows([...rowsRef.current, ...newRows]);
        } catch (error) {
            console.error("clipboard error:", error);
        }
    };

    const handleDelete = (uid) => {
        setDeleting(true);
        setTimeout(() => {
            updateRows(rowsRef.current.filter((row) => row.uid !== uid));
            setDeleting(false);
        }, 100);
    };

    const columns = [
        { field: "id", headerName: "ID", hide: true },
        { field: "uid", headerName: "UID", hide: true },
        { field: "code", headerName: "KEY", width: 300, editable: true },
        { field: "name", headerName: "NAME", width: 300, editable: true },
        { field: "unit", headerName: t('message.common.subtitle.unit'), width: 120, editable: true },
        {
            field: "actions",
            headerName: "",
            editable: false,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                !params.row.id && (
                    <LoadingButton
                        tabIndex={-1}
                        variant="contained"
                        color="secondary"
                        loading={deleting}
                        size="small"
                        onClick={async () => {
                            if (document.activeElement) {
                                document.activeElement.blur();
                            }

                            const rowId = params.row.uid; 

                            const currentMode = apiRef.current.getRowMode(rowId);
                            if (currentMode === 'edit') {
                                await apiRef.current.stopRowEditMode({
                                    id: rowId,
                                    ignoreModifications: false,
                                });
                            }

                            handleDelete(rowId);
                        }}
                    >{t('message.common.btn.delete')}</LoadingButton>
                )
            ),
        },
    ];

    return (
        <>
            <Button variant="contained" onClick={() => setOpen(true)}>
                {t('message.common.btn.spec.bulk.register')}
            </Button>

            <Modal className="pop_box" open={open} onClose={() => setOpen(false)}>
                <MainCard modal darkTitle content={false} style={{ width: "1300px" }}>
                    <div style={{display:"flex", justifyContent:"space-between", alignItems:"center"}}>
                        <div>
                            <span className='bullet'><img src={`${process.env.PUBLIC_URL}/img/icon/popbullet.png`} alt='' /></span>&nbsp;
                            <Chip label={`${t('message.common.btn.spec.bulk.register')}`} color="primary" style={{maxWidth:"400px"}}/>
                        </div>

                        <div>
                            <Button variant="contained" color="primary" size="small" onClick={() => setOpen(false)}>
                                {t('message.common.btn.close')}
                            </Button>
                        </div>
                    </div>

                    <CardContent>
                        <div className="scroll_table scroll_table1" style={{ padding: "10px", color: "#000" }}>
                            <div style={{ display:"flex", flexDirection:"row", flexWrap:"wrap", justifyContent:"flex-start", alignItems:"flex-start", width: "20%"}}>
                                <div style={{ padding: "5px" }}>
                                    <Button variant="contained" color="primary" onClick={handlePaste} sx={{ mb: 2 }}>
                                        {t('message.common.btn.paste.clipboard')}
                                    </Button>

                                    <div style={{ padding: "5px", color:"#fff" }}>
                                    * {t('message.common.guide.paste.instruction')}<br />
                                    * {t('message.common.guide.save.reminder')}
                                    </div>
                                </div>
                            </div>
                            <TableContainer className="spec_table">
                                <ThemeProvider theme={theme}>
                                    <DataGridPro
                                        apiRef={apiRef}
                                        rows={rows}
                                        columns={columns}
                                        localeText={muiLocaleMap[i18n.language] ?? enUS.components.MuiDataGrid.defaultProps.localeText}
                                        sx={{
                                            height: 500,
                                            '& .MuiDataGrid-columnHeaders': {
                                                backgroundColor: '#f0f0f0',
                                                fontWeight: 'bold',
                                            },
                                            '& .MuiDataGrid-row:hover': {
                                                backgroundColor: '#f9f9f9',
                                            },
                                            '& .MuiDataGrid-rowCount': {
                                                color: '#fff'
                                            },
                                            '& .MuiDataGrid-cell.MuiDataGrid-cell--editing': {
                                                backgroundColor: 'unset'
                                            }
                                        }}
                                        getRowId={(row) => row.uid}
                                        columnVisibilityModel={{ id: false, uid: false }}
                                        experimentalFeatures={{ newEditingApi: true }}
                                        processRowUpdate={(updatedRow) => {
                                            const newRows = rowsRef.current.map((row) =>
                                                row.uid === updatedRow.uid ? { ...row, ...updatedRow } : row
                                            );
                                            updateRows(newRows);
                                            return updatedRow;
                                        }}
                                        pagination={false}
                                        disableSelectionOnClick
                                    />
                                </ThemeProvider>
                                <div className="btn_right">
                                    <Button variant="contained" color="primary" onClick={handleSave} sx={{ mb: 2 }}>
                                    {t('message.common.btn.save.changes')}
                                    </Button>
                                </div>
                            </TableContainer>
                        </div>
                    </CardContent>
                </MainCard>
            </Modal>
        </>
    );
};

export default MasterSpecWriteLayer;
